import { useRef, useState } from "react";
import { CheckCircle2, Download, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { ukDateTime } from "../lib/format";
import { Loading, Panel } from "./common";
import { Button } from "./ui/button";
import { DrawdownTable } from "../pages/Drawdowns";

export async function openDoc(id, name) {
  const { data } = await api.get(`/documents/${id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function ProjectDocs({ projectId }) {
  const { data, loading, reload } = useApi(`/projects/${projectId}/documents`);
  const { isPartner } = useAuth();
  const [type, setType] = useState(isPartner ? "invoice" : "signed_proforma");
  const ref = useRef();
  if (loading && !data) return <Loading />;
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("project_id", projectId); fd.append("type", type);
    try { await api.post("/documents", fd); toast.success("Uploaded"); reload(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const types = Object.entries(data.types).filter(([k]) => !isPartner || ["invoice", "drawdown_request", "partner_return", "final_account", "other"].includes(k));
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <Panel title="Project File checklist" testid="project-file-checklist">
        <ul className="space-y-2">{data.checklist.map((c) => <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`checklist-${c.key}`} data-present={c.present}>
          {c.present ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--positive))]" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}{c.label}<span className="ml-auto text-xs text-muted-foreground">{c.present ? "Present" : "Missing"}</span></li>)}</ul>
      </Panel>
      <Panel title={`Documents · ${data.documents.length}`} testid="project-documents" actions={<div className="flex gap-2">
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select">{types.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <input ref={ref} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="doc-upload-input" />
        <Button size="sm" onClick={() => ref.current?.click()} data-testid="doc-upload-btn"><Upload className="mr-1 h-4 w-4" /> Upload</Button></div>}>
        <ul className="divide-y">{data.documents.map((d) => <li key={d.id} className="flex items-center gap-3 py-2 text-sm" data-testid="document-row">
          <div className="min-w-0 flex-1"><div className="truncate font-semibold">{d.filename}</div><div className="text-xs text-muted-foreground">{d.type_label} · {d.uploaded_by} · {ukDateTime(d.uploaded_at)}</div></div>
          <button onClick={() => openDoc(d.id, d.filename).catch((e) => toast.error(errMsg(e)))} className="text-muted-foreground hover:text-foreground" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></button></li>)}
          {!data.documents.length && <li className="py-6 text-sm text-muted-foreground">No documents yet.</li>}</ul>
      </Panel>
    </div>
  );
}

export function ProjectDrawdowns({ projectId }) {
  const { data } = useApi("/drawdowns", { params: { project_id: projectId } });
  return data ? <DrawdownTable rows={data} /> : <Loading />;
}
